import React, { useCallback } from 'react';
import { useIntl } from 'umi';
import moment from 'moment';
import classNames from 'classnames';
import { Dropdown, Input, List, } from 'tea-component';
import { IUserFilterItem, IUseFilterRenderersProps, IUseFilterRenderersReturn } from '../type';
import { OPERATOR_OPTIONS } from '../constant';
import CountryFilter from '@/pages/benchmark/components/CountryFilter';
import SvgIcon from '@home/components/SvgIcon';
import StaticDate from '@/pages/datalab/components/DateComponents/StaticDate';
import { DateGranularity } from '@dbl/core/date';
import { DateValue } from '@/pages/datalab/components/DateComponents/DynamicDate';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLanguage } from '@home/utils/common';
import FilterSelect from '@/pages/dashboard/components/FilterSelect';
import CommonBubble from '@home/components/CommonBubble';
import Abnormal from '@home/components/Abnormal';

export const useFilterRenderers = ({
  isConfirm,
  filters,
  fieldOption,
  stringDataMap,
  stringLoading,
  errorInfo: propsErrorInfo,
  handleFilterChange,
  handleRemoveFilter,
  getStringData,
}: IUseFilterRenderersProps): IUseFilterRenderersReturn => {
  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  // 错误信息
  const errorInfo = propsErrorInfo || getLanguage(isEn, 'Cannot be empty', '不能为空');
  // 渲染字段选择器
  const renderFieldSelector = useCallback(
    (filter: IUserFilterItem) => {
      const { id, hasError, field } = filter || {};
      // 提交时，是否错误和为空，用于提示
      const isError = isConfirm && hasError && !field;
      return <CountryFilter
        className={classNames('dbl-filter-field-selector', {
          'dbl-filter-error': isError
        })}
        bubbleProps={{
          overlayClassName: classNames('dbl-filter-panel-overlay', {
            'dbl-filter-panel-overlay-error': isError
          })
        }}
        overlayClassName={classNames('dbl-filter-field-selector-overlay')}
        defaultOpen={true}
        sort={false}
        placementOffset={[-10, 10]}
        nameCN={''}
        nameEN={''}
        tooltipEn={isError ? 'Cannot be empty' : ""}
        tooltipCn={isError ? '不能为空' : ""}
        trigger={"hover"}
        placeholderCn={'用户属性'}
        placeholderEn={'User Property'}
        value={field}
        filterKey={'field'}
        options={fieldOption}
        filter={(inputValue, option) => {
          const { value, disabled } = option || {};
          if (disabled || value === 'all') return false;
          // 中英文都匹配
          const { nameEn = '', nameCn = '' } = option || {};
          const val = inputValue?.toLocaleLowerCase();
          if (nameEn?.toLocaleLowerCase()?.includes(val) || nameCn?.toLocaleLowerCase()?.includes(val)) {
            return true;
          }
          return false;
        }}
        onChange={(filterKey, value) => {
          const fieldItem = fieldOption?.find((f) => f?.code === value);
          if(!fieldItem) return;
          const {
            type: fieldType = 'string',
            filter_id: filterId = '',
            name_en: nameEn,
            name_cn: nameCn,
            description_en: descriptionEn,
            description_cn: descriptionCn,
            is_mapped: isMapped = false,
          } = fieldItem || {};
          const updated: IUserFilterItem = {
            // 后端协议
            filter_id: filterId,
            name_en: nameEn,
            name_cn: nameCn,
            description_en: descriptionEn,
            description_cn: descriptionCn,
            data_type: fieldType,
            is_mapped: isMapped,
            // 前端协议
            id,
            fieldType,
            field: value,
            operator: undefined,
            value: undefined
          };
          // 当字段变化时，重置操作符和值
          if (fieldType === 'string') {
            updated.operator = 'include';
            updated.operatorEn = 'Include';
            updated.operatorCn = '包含';
            getStringData?.(fieldItem?.filter_id || "");
          } else if (fieldType === 'number') {
            updated.operator = 'greater';
            updated.operatorEn = 'Greater(＞)';
            updated.operatorCn = '大于(＞)';
          } else if (fieldType === 'date') {
            updated.operator = 'between';
            updated.operatorEn = 'Between';
            updated.operatorCn = '区间';
            updated.dateType = DateGranularity.DAY;
            updated.value = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')];
          }
          handleFilterChange(id, updated);
        }}
      />;
    },
    [fieldOption, getStringData, handleFilterChange, isConfirm]
  );

  // 渲染操作符选择器
  const renderOperatorSelector = useCallback(
    (filter: IUserFilterItem) => {
      const { fieldType, operator, is_mapped: isMapped, hasError } = filter || {};
      // 日期类型不需要操作符
      if (fieldType === 'date') {
        return null;
      }

      const defaultOptions = OPERATOR_OPTIONS?.[fieldType || 'default'] || [];
      const options = isMapped ? defaultOptions.filter((op) => !['notNull', 'null']?.includes(op?.value)) : defaultOptions;
      const selectedOperator = options?.find((op) => op?.value === operator);
      const buttonText = operator ? getLanguage(isEn, selectedOperator?.text_en, selectedOperator?.text) :
        <div className='dbl-filter-operator-selector-placeholder'>
          {formatMessage({ id: 'dbl_filter_conditions' })}
        </div>;
      // 提交时，是否错误和为空，用于提示
      const isError = isConfirm && hasError && !operator;
      return (
        <CommonBubble
          overlayClassName={classNames('dbl-filter-panel-overlay', {
            'dbl-filter-panel-overlay-error': isError
          })}
          content={isError ? errorInfo : ""}
        >
          <Dropdown
            className={classNames('dbl-filter-operator-selector', {
              'dbl-filter-error': isError
            })}
            overlayClassName='dbl-filter-operator-selector-overlay'
            appearance="button"
            button={buttonText}
          >
            {(close) => (
              <List type="option">
                {
                  options?.length > 0 ? options?.map((op) => (
                    <List.Item
                      className={classNames({ 'is-selected': op?.value === operator })}
                      key={op?.value}
                      onClick={() => {
                        const updated: Partial<IUserFilterItem> = {
                          operator: op?.value,
                          operatorEn: op?.text_en || op?.text,
                          operatorCn: op?.text_cn || op?.text,
                          value: undefined,
                        };
                        if (['notNull', 'null']?.includes(op?.value || "")) {
                          updated.value = [];
                          updated.valueEn = [];
                          updated.valueCn = [];
                        }
                        handleFilterChange(filter?.id, updated);
                        close();
                      }}
                    >
                      {getLanguage(isEn, op?.text_en, op?.text)}
                    </List.Item>
                  )) : <List.Item className='dbl-filter-operator-selector-overlay-no-data'>
                    {formatMessage({ id: 'dbl_filter_please_select_user_property_first' })}
                  </List.Item>
                }
              </List>
            )}
          </Dropdown>
        </CommonBubble>
      );
    },
    [errorInfo, formatMessage, handleFilterChange, isConfirm, isEn]
  );

  // 渲染值输入
  const renderValueInput = useCallback(
    (filter: IUserFilterItem) => {
      const { filter_id: filterId, fieldType, id, operator, dateType, name_en: nameEn, name_cn: nameCn, hasError, value } = filter;
      // 无数值不展示输入框
      if (['notNull', 'null']?.includes(operator || "")) {
        return null;
      }
      // 提交时，是否错误和为空，用于提示
      const isError = isConfirm && hasError;
      // 字符串下拉选
      if (!fieldType || fieldType === 'string') {
        const stringData = stringDataMap?.[filterId || ""] || [];
        // 是否获取数据中，用于展示loading
        const isGetDataState = stringData?.length === 0 && stringLoading;
        const options = stringData?.map(item => ({
          value: item?.code,
          value_en: item?.value_en,
          value_cn: item?.value_cn,
          text: getLanguage(isEn, item?.value_en, item?.value_cn),
          title: getLanguage(isEn, item?.value_en, item?.value_cn)
        }));
        return (<FilterSelect
          className={classNames('dbl-filter-string-selector', {
            'dbl-filter-error': isError && !value
          })}
          overlayClassName={classNames({ 'dbl-filter-string-selector-state': isGetDataState })}
          // 只展示loading状态
          stateTips={
            isGetDataState && <Abnormal
              style={{ height: '72px' }}
              isLoading={stringLoading}
              isError={false}
              isNoData={false}
            />
          }
          bubbleProps={{
            overlayClassName: classNames('dbl-filter-panel-overlay', {
              'dbl-filter-panel-overlay-error': isError
            })
          }}
          locale={locale}
          clickClose={false}
          listWidth={350}
          value={value || []}
          onChange={(value) => {
            const newValue: string[] = [];
            const valueEn: string[] = [];
            const valueCn: string[] = [];
            // 按照options的顺序展示
            options?.forEach(item => {
              if (value?.includes(item?.value)) {
                newValue?.push(item?.value);
                valueEn?.push(item?.value_en || item?.value);
                valueCn?.push(item?.value_cn || item?.value);
              }
            });
            handleFilterChange(id, {
              value: newValue,
              valueEn,
              valueCn,
            });
          }}
          name={getLanguage(isEn, nameEn, nameCn)}
          placeholder={formatMessage({ id: 'dbl_filter_value' })}
          tooltip={isError && !value ? errorInfo : ""}
          options={options}
        />);
      }
      // 数字输入
      if (fieldType === 'number') {
        // 范围输入
        if (operator === 'between') {
          // 范围输入
          const rangeValue = value as number[] | null;
          return (
            <div className={classNames('dbl-filter-value-range')}>
              <CommonBubble
                overlayClassName={classNames('dbl-filter-panel-overlay', {
                  'dbl-filter-panel-overlay-error': isError
                })}
                content={isError && !rangeValue?.[0] ? errorInfo : ""}
              >
                <Input
                  className={classNames({
                    'dbl-filter-error': isError && !rangeValue?.[0]
                  })}
                  type="number"
                  value={rangeValue?.[0]?.toString() || ''}
                  placeholder={formatMessage({ id: 'dbl_filter_number' })}
                  onChange={(value) => {
                    const currentRange = rangeValue || ["", ""];
                    handleFilterChange(id, {
                      value: [value || "", currentRange[1]],
                      valueEn: [value || "", currentRange[1]],
                      valueCn: [value || "", currentRange[1]],
                    });
                  }}
                />
              </CommonBubble>
              <span className="dbl-filter-value-separator">-</span>
              <CommonBubble
                overlayClassName={classNames('dbl-filter-panel-overlay', {
                  'dbl-filter-panel-overlay-error': isError
                })}
                content={isError && !rangeValue?.[1] ? errorInfo : ""}
              >
                <Input
                  className={classNames({
                    'dbl-filter-error': isError && !rangeValue?.[1]
                  })}
                  type="number"
                  value={rangeValue?.[1]?.toString() || ''}
                  placeholder={formatMessage({ id: 'dbl_filter_number' })}
                  onChange={(value) => {
                    const currentRange = rangeValue || ["", ""];
                    handleFilterChange(id, {
                      value: [currentRange[0], value || ""],
                      valueEn: [currentRange[0], value || ""],
                      valueCn: [currentRange[0], value || ""],
                    });
                  }}
                />
              </CommonBubble>
            </div>
          );
        }
        // 单值输入
        return (
          <CommonBubble
            overlayClassName={classNames('dbl-filter-panel-overlay', {
              'dbl-filter-panel-overlay-error': isError
            })}
            content={isError ? errorInfo : ""}
          >
            <Input
              className={classNames({
                'dbl-filter-error': isError
              })}
              type="number"
              value={Array.isArray(value) ? value[0]?.toString() : ''}
              placeholder={formatMessage({ id: 'dbl_filter_number' })}
              onChange={(value) => {
                handleFilterChange(id, {
                  value: [value || ""],
                  valueEn: [value || ""],
                  valueCn: [value || ""],
                });
              }}
            />
          </CommonBubble>
        );
      }
      // 日期选择器
      if (fieldType === 'date') {
        // 日期选择器
        const [start, end] = value || [moment()?.format('YYYY-MM-DD'), moment()?.format('YYYY-MM-DD')];
        const dateValue = {
          dateRange: [start, end],
          dateType: dateType || 'day',
          quickValue: filter?.quickValue || {},
          compareTimes: filter?.compareTimes || [],
        };
        return (<StaticDate
          value={dateValue}
          isDisabledCompare={true}
          onChange={({ dateRange, dateType, compareTimes, quickValue }: DateValue) => {
            handleFilterChange(id, {
              value: dateRange,
              valueEn: dateRange,
              valueCn: dateRange,
              dateType,
              compareTimes,
              quickValue,
              operator: 'between',
            });
          }}
        />);
      }
      return null;
    },
    [isConfirm, stringDataMap, stringLoading, locale, isEn, formatMessage, errorInfo, handleFilterChange]
  );

  // 渲染筛选条件项
  const renderFilterItem = useCallback(
    (filter: IUserFilterItem) => (
      <div key={filter?.id} className="dbl-filter-panel-item">
        <div className="dbl-filter-panel-item-content">
          {renderFieldSelector(filter)}
          {renderOperatorSelector(filter)}
          {renderValueInput(filter)}
          <CommonBubble
            content={formatMessage({ id: 'dbl_common_delete' })}
            dark
            placement="top"
          >
            <div>
              <SvgIcon type='trash-can-icon' className="dbl-filter-panel-item-delete" onClick={() => handleRemoveFilter(filter?.id)} />
            </div>
          </CommonBubble>
        </div>
      </div>
    ),
    [filters, renderFieldSelector, renderOperatorSelector, renderValueInput, handleRemoveFilter, formatMessage]
  );

  return {
    renderFieldSelector,
    renderOperatorSelector,
    renderValueInput,
    renderFilterItem,
  };
};

